import { mediaSettingsApi } from "@circle-react/api/mediaSettingsApi";
import type { UpdateMediaSettingsPayload } from "@circle-react/api/mediaSettingsApi";
import type { ApiError } from "@circle-react/config/CustomErrors";
import type { MediaSetting } from "@circle-react/types/MediaSetting";
import { useMutation, useQuery, useQueryClient } from "react-query";

import { t } from "@/i18n-js/instance";
import { useToast } from "@/react/components/SettingsApp/CompassCompat/Toast";

export const useMediaSettings = (signedId: string, enabled: boolean = true) => {
  const { success, error } = useToast();
  const queryClient = useQueryClient();
  const {
    data: mediaSettings,
    isInitialLoading: isLoadingMediaSettings,
    refetch: refetchMediaSettings,
  } = useQuery({
    queryKey: ["mediaSettings", signedId],
    queryFn: () => mediaSettingsApi.show(signedId),
    enabled: !!signedId && enabled,
  });

  const { mutate: updateMediaSettings, isPending: isUpdatingMediaSettings } =
    useMutation<MediaSetting, ApiError, UpdateMediaSettingsPayload>({
      mutationFn: formData => mediaSettingsApi.update(signedId, formData),
      onSuccess: () => {
        success(t("media_settings.success"));
        void refetchMediaSettings();
        void queryClient.invalidateQueries({
          queryKey: ["mediaSettings", signedId],
        });
      },
      onError: responseError => {
        error(t("media_settings.error"));
        console.error(responseError);
      },
    });

  return {
    mediaSettings,
    isLoading: isLoadingMediaSettings,
    isUpdating: isUpdatingMediaSettings,
    update: updateMediaSettings,
    refetch: refetchMediaSettings,
  };
};
